import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import NewRelicIcon from "src/components/ui/icons/newrelic";
import { getProductName } from "src/utils/brandTheme";

import NewRelicIntegrationForm from "./NewRelicIntegrationForm";

interface NewRelicIntegrationProps {
  isOpen: boolean;
  onClose: () => void;
  onRefetch?: () => void;
  brandTheme?: string | null;
}

const NewRelicInstructions: React.FC<{ productName: string }> = ({ productName }) => {
  return (
    <div className="text-muted-foreground space-y-3">
      <div className="mb-4">
        <p className="mb-2 font-bold">API Key Instructions:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            Log into your New Relic account.
          </li>
          <li>
            Navigate to <span className="text-amber-600">Administration</span>
            {" > "}
            <span className="text-amber-600">API keys</span> in the main menu.
          </li>
          <li>
            Click on <span className="text-amber-600">Create a key</span>.
          </li>
          <li>
            Select <span className="text-amber-600">User</span> as the key type.
          </li>
          <li>
            Enter a name for your key (e.g., "{productName} Integration") and click{" "}
            <span className="text-amber-600">Create key</span>.
          </li>
          <li>
            Copy the generated API Key and paste it into the API Key field below.
          </li>
        </ol>
      </div>

      <div className="mb-4">
        <p className="mb-2 font-bold">Account ID Instructions:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            Your account ID can be found in your New Relic account URL, e.g.{" "}
            <span className="font-mono text-green-400">https://one.newrelic.com/nr1-core?account=ACCOUNT_ID</span>.
          </li>
          <li>
            Copy your account ID and paste it into the Account ID field below.
          </li>
        </ol>
      </div>

      <div className="mb-4">
        <p className="mb-2 font-bold">Region Configuration:</p>
        <ul className="text-sm space-y-1 list-disc list-inside">
          <li>
            For US region: Use <span className="font-mono text-green-400">US</span>
          </li>
          <li>
            For EU region: Use <span className="font-mono text-green-400">EU</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default function NewRelicIntegration({ isOpen, onClose, onRefetch, brandTheme }: NewRelicIntegrationProps) {
  const [showInstructions, setShowInstructions] = useState(false);
  const productName = getProductName(brandTheme);

  const handleFormComplete = (success: boolean) => {
    if (success) {
      onClose();
      onRefetch?.();
    }
  };

  const handleFormCancel = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl bg-popover text-foreground border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <NewRelicIcon className="h-6 w-6" />
            Connect New Relic
          </DialogTitle>
          <DialogDescription>
            Connect your New Relic account to give {productName} access.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 overflow-y-auto max-h-[80vh] p-1">
          <div className="space-y-3">
            <button
              onClick={() => setShowInstructions(!showInstructions)}
              className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
            >
              {showInstructions ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              {showInstructions ? "Hide instructions" : "Show instructions"}
            </button>

            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
              showInstructions
                ? "max-h-[2000px] opacity-100"
                : "max-h-0 opacity-0"
            }`}>
              <NewRelicInstructions productName={productName} />
            </div>
          </div>

          <NewRelicIntegrationForm
            onComplete={handleFormComplete}
            onCancel={handleFormCancel}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}